import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTickets, updateTicketStatus } from '../api/tickets';
import { assignConversation } from '../api/conversations';
import { useSocket } from '../hooks/useSocket';
import { Topbar } from '../layout/Topbar';
import type { Ticket } from '../types';

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em andamento',
  WAITING_CUSTOMER: 'Aguardando cliente',
  RESOLVED: 'Resolvido',
  CLOSED: 'Fechado',
};

const PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Baixa',
  NORMAL: 'Normal',
  HIGH: 'Alta',
  URGENT: 'Urgente',
};

function priorityClass(p: string): string {
  return { LOW: 'neutral', NORMAL: 'neutral', HIGH: 'warning', URGENT: 'danger' }[p] || 'neutral';
}

function statusClass(s: string): string {
  return { OPEN: 'warning', IN_PROGRESS: 'primary', WAITING_CUSTOMER: 'neutral', RESOLVED: 'success', CLOSED: 'neutral' }[s] || 'neutral';
}

export function TicketsPage() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const { on } = useSocket();

  useEffect(() => {
    getTickets()
      .then(setTickets)
      .catch(() => console.error('Falha ao carregar tickets'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const unsub = on('ticket.status_changed', (tkt: any) => {
      setTickets((prev: Ticket[]) => prev.map((t) => (t.id === tkt.id ? { ...t, ...tkt } : t)));
      setSelectedTicket((prev: Ticket | null) => prev && prev.id === tkt.id ? { ...prev, ...tkt } : prev);
    });
    return unsub;
  }, [on]);

  const handleStatusChange = async (id: string, status: string) => {
    try {
      const updated = await updateTicketStatus(id, status as Ticket['status']);
      setTickets((prev) => prev.map((t) => (t.id === id ? updated : t)));
      setSelectedTicket((prev) => prev && prev.id === id ? updated : prev);
    } catch (err) { console.error('Falha ao atualizar status do ticket:', err); }
  };

  const handleStartConversation = async () => {
    if (!selectedTicket) return;
    try {
      const updatedTicket = await updateTicketStatus(selectedTicket.id, 'IN_PROGRESS');
      await assignConversation(selectedTicket.conversationId);
      setTickets((prev) => prev.map((ticket) => ticket.id === updatedTicket.id ? updatedTicket : ticket));
      setSelectedTicket(updatedTicket);
      navigate(`/inbox?conversation=${encodeURIComponent(selectedTicket.conversationId)}`);
    } catch (err) {
      console.error('Falha ao assumir atendimento:', err);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="Tickets" subtitle="Protocolos, prioridade e status dos atendimentos." />

      <div className="records-layout">
        <div className="panel">
          <div className="panel-header">
            <h2>Tickets</h2>
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : tickets.length === 0 ? (
            <div className="empty-state"><p>Nenhum ticket encontrado.</p></div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Assunto</th>
                  <th>Setor</th>
                  <th>Prioridade</th>
                  <th>Status</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    style={{ background: selectedTicket?.id === ticket.id ? 'var(--color-primary-bg)' : undefined }}
                  >
                    <td><code style={{ fontSize: '0.857rem' }}>{ticket.protocol}</code></td>
                    <td>{ticket.subject}</td>
                    <td>{ticket.department?.name || '—'}</td>
                    <td><span className={`pill ${priorityClass(ticket.priority)}`}>{PRIORITY_LABELS[ticket.priority]}</span></td>
                    <td><span className={`pill ${statusClass(ticket.status)}`}>{STATUS_LABELS[ticket.status]}</span></td>
                    <td style={{ color: 'var(--text-tertiary)', fontSize: '0.857rem' }}>
                      {new Date(ticket.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selectedTicket && (
          <div className="panel">
            <div className="panel-header">
              <h2>Ticket {selectedTicket.protocol}</h2>
            </div>
            <div className="panel-body">
              <div className="customer-details">
                <div className="detail-row">
                  <span className="label">Protocolo</span>
                  <span className="value" style={{ fontFamily: 'var(--font-mono)' }}>{selectedTicket.protocol}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Assunto</span>
                  <span className="value">{selectedTicket.subject}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Setor</span>
                  <span className="value">{selectedTicket.department?.name || '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Cliente</span>
                  <span className="value">{selectedTicket.customer?.name || '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Atendente</span>
                  <span className="value">{selectedTicket.assignedAgent?.name || '—'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Prioridade</span>
                  <span className="value">
                    <span className={`pill ${priorityClass(selectedTicket.priority)}`}>
                      {PRIORITY_LABELS[selectedTicket.priority]}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Status</span>
                  <span className="value">
                    <span className={`pill ${statusClass(selectedTicket.status)}`}>
                      {STATUS_LABELS[selectedTicket.status]}
                    </span>
                  </span>
                </div>
                {selectedTicket.closedAt && (
                  <div className="detail-row">
                    <span className="label">Fechado em</span>
                    <span className="value">
                      {new Date(selectedTicket.closedAt).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>

              <div className="action-stack" style={{ marginTop: 16 }}>
                <button
                  onClick={handleStartConversation}
                  disabled={selectedTicket.status === 'IN_PROGRESS' || selectedTicket.status === 'CLOSED'}
                >
                  Iniciar atendimento
                </button>
                <button
                  className="secondary"
                  onClick={() => handleStatusChange(selectedTicket.id, 'WAITING_CUSTOMER')}
                  disabled={selectedTicket.status === 'CLOSED'}
                >
                  Aguardar cliente
                </button>
                <button
                  className="secondary"
                  onClick={() => handleStatusChange(selectedTicket.id, 'RESOLVED')}
                  disabled={selectedTicket.status === 'CLOSED' || selectedTicket.status === 'RESOLVED'}
                >
                  Resolver
                </button>
                <button
                  className="danger"
                  onClick={() => handleStatusChange(selectedTicket.id, 'CLOSED')}
                  disabled={selectedTicket.status === 'CLOSED'}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
